/** Public Flights transport. Keep in sync with cbs-admin/src/api/flight-client.ts. */
export class FlightApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly retryAfterSeconds = 0,
    public readonly traceId?: string,
  ) {
    super('Flight lookup failed.');
    this.name = 'FlightApiError';
    Object.setPrototypeOf(this, FlightApiError.prototype);
  }
}

export function flightErrorMessage(error: unknown): string {
  if (error instanceof FlightApiError) {
    const wait = error.retryAfterSeconds > 0
      ? ` Please try again in ${Math.ceil(error.retryAfterSeconds / 60)} minute(s).` : ' Please try again later.';
    if (error.code === 'FLIGHT_DAILY_QUOTA_EXCEEDED')
      return 'Live flight lookups have reached today’s limit. New lookups resume at midnight Vietnam time.';
    if (error.status === 429) return 'Too many flight lookups.' + wait;
    if (error.status === 403) return 'Unable to initialize flight lookup. Please reload the page and try again.';
    if (error.status === 400) return 'Please check the flight number, airport and date.';
    if (error.status === 503) return 'Flight lookup is temporarily unavailable.' + wait;
  }
  return 'Unable to load flight information. Please try again later.';
}

interface Context {
  deviceToken: string;
  sessionToken: string;
  deviceExpiresAt: string;
  sessionExpiresAt: string;
  fingerprint: string;
}

// A supporting browser signal, not proof of a unique physical device or a human.
// No random ID: clearing storage must not create a new fingerprint on every request.
export async function browserFingerprint(): Promise<string> {
  const traits = JSON.stringify([
    'flight-browser-v1', navigator.userAgent, navigator.platform, navigator.languages,
    navigator.hardwareConcurrency, navigator.maxTouchPoints,
    [globalThis.screen.width, globalThis.screen.height].sort((a, b) => a - b), globalThis.screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ]);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(traits));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function createFlightClient(apiBase: string, options: {
  fetch?: typeof fetch;
  fingerprint?: () => Promise<string>;
  now?: () => number;
} = {}) {
  const base = new URL(apiBase);
  const storageKey = `flight-context:v1:${base.origin}`;
  const send = options.fetch || ((...args: Parameters<typeof fetch>) => fetch(...args));
  const now = options.now || Date.now;
  let fingerprintPromise: Promise<string> | undefined;
  let context: Context | undefined;
  let initializing: Promise<Context> | undefined;
  let cooldown: { until: number; error: FlightApiError } | undefined;
  const pending = new Map<string, Promise<unknown>>();
  const read = (storage: 'localStorage' | 'sessionStorage'): Partial<Context> | undefined => {
    try { return JSON.parse(globalThis[storage].getItem(storageKey) || 'null') || undefined; }
    catch { return undefined; }
  };
  const write = (storage: 'localStorage' | 'sessionStorage', value: Partial<Context> | undefined) => {
    try {
      if (value) globalThis[storage].setItem(storageKey, JSON.stringify(value));
      else globalThis[storage].removeItem(storageKey);
    } catch { /* Blocked browser storage: keep the signed tokens in memory. */ }
  };

  function checkCooldown() {
    if (cooldown && cooldown.until > now()) {
      throw new FlightApiError(cooldown.error.status, cooldown.error.code,
        Math.ceil((cooldown.until - now()) / 1000), cooldown.error.traceId);
    }
    cooldown = undefined;
  }

  async function decode(response: Response) {
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const header = response.headers.get('Retry-After');
      const raw = header && !Number.isFinite(Number(header))
        ? (Date.parse(header) - now()) / 1000 : Number(header || body?.retryAfterSeconds || 0);
      const retry = Number.isFinite(raw) ? Math.max(0, Math.ceil(raw)) : 0;
      const error = new FlightApiError(response.status, body?.code || 'FLIGHT_REQUEST_FAILED', retry, body?.traceId);
      // Daily quota does not block different queries which the server can still serve from cache.
      if ((error.status === 429 && error.code !== 'FLIGHT_DAILY_QUOTA_EXCEEDED') || error.status === 503)
        cooldown = { until: now() + Math.max(1, retry) * 1000, error };
      throw error;
    }
    if (body === null) throw new FlightApiError(502, 'FLIGHT_INVALID_RESPONSE');
    return body;
  }

  const fingerprint = () => fingerprintPromise ||= (options.fingerprint || browserFingerprint)();

  async function initialize(force = false): Promise<Context> {
    checkCooldown();
    if (initializing) return initializing;
    initializing = (async () => {
      const hash = await fingerprint();
      if (!/^[A-Za-z0-9_-]{16,128}$/.test(hash)) throw new FlightApiError(403, 'FLIGHT_CLIENT_CONTEXT_INVALID');
      const session = context || read('sessionStorage');
      const device = context || read('localStorage');
      if (!force && session?.sessionToken && session.deviceToken && session.fingerprint === hash &&
          Date.parse(session.sessionExpiresAt || '') > now() + 30000 &&
          Date.parse(session.deviceExpiresAt || '') > now() + 30000) {
        context = session as Context;
        return context;
      }
      async function issue(useExisting: boolean): Promise<Context> {
        checkCooldown();
        const tokens = await decode(await send(new URL('/api/flights/client-context', base).href, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fingerprint: hash,
            deviceToken: useExisting ? device?.deviceToken : undefined,
            sessionToken: useExisting ? session?.sessionToken : undefined }),
        }));
        if (!tokens.deviceToken || !tokens.sessionToken || !Number.isFinite(Date.parse(tokens.sessionExpiresAt)) ||
            !Number.isFinite(Date.parse(tokens.deviceExpiresAt))) throw new FlightApiError(502, 'FLIGHT_INVALID_RESPONSE');
        return { ...tokens, fingerprint: hash };
      }
      try { context = await issue(true); }
      catch (error) {
        if (!(error instanceof FlightApiError) || error.status !== 403 || !error.code.startsWith('FLIGHT_CLIENT_CONTEXT_')) throw error;
        // Only a rejected context may be reset. A valid cookie remains the device authority.
        // The backend clears an unreadable cookie so one bootstrap can recover after key loss.
        context = undefined;
        write('localStorage', undefined);
        write('sessionStorage', undefined);
        context = await issue(false);
      }
      write('localStorage', { deviceToken: context.deviceToken, deviceExpiresAt: context.deviceExpiresAt });
      write('sessionStorage', context);
      return context;
    })();
    try { return await initializing; } finally { initializing = undefined; }
  }

  async function search<T>(route: string, body: unknown, allDay?: boolean): Promise<T> {
    if (!/^(status|schedule)\/(airport|flight)\/[a-z-]+$/.test(route)) throw new Error('Invalid flight route.');
    checkCooldown();
    const key = JSON.stringify([route, body, allDay]);
    if (pending.has(key)) return pending.get(key) as Promise<T>;
    if (pending.size >= 64) throw new FlightApiError(503, 'FLIGHT_SERVICE_BUSY', 1);
    const execute = async (renew: boolean): Promise<T> => {
      const tokens = await initialize(renew);
      checkCooldown();
      const url = new URL(`/api/flights/${route}`, base);
      if (allDay !== undefined) url.searchParams.set('allDay', String(allDay));
      return decode(await send(url.href, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Flight-Session': tokens.sessionToken,
          'X-Flight-Device': tokens.deviceToken, 'X-Flight-Fingerprint': tokens.fingerprint },
        body: JSON.stringify(body),
      }));
    };
    const request = (async () => {
      try { return await execute(false); }
      catch (error) {
        if (error instanceof FlightApiError && error.status === 403 && error.code.startsWith('FLIGHT_CLIENT_CONTEXT_'))
          return execute(true);
        throw error; // Never automatically retry 429, 503, timeouts or network errors.
      }
    })();
    pending.set(key, request);
    try { return await request; } finally { pending.delete(key); }
  }
  return { initialize, search };
}

const clients = new Map<string, ReturnType<typeof createFlightClient>>();
export function getFlightClient(apiBase: string) {
  const origin = new URL(apiBase).origin;
  let client = clients.get(origin);
  if (!client) { client = createFlightClient(origin); clients.set(origin, client); }
  return client;
}
